import * as React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import PrintButton from "../../components/PrintButton";
import { getIndexAction } from "../../store/actions/General";
import { handleDate } from "../../utils/utils";
import Table from "../Family/components/Table";
import AboutTable from "./components/AdoutTable";
import ColumnChart from "./components/ColumnChart";
import CurrentCompTable from "./components/CurrentCompTable";
import DailyDataCalendar from "./components/DailyDataCalendar";
import ExpectedCompTable from "./components/ExpectedCompTable";
import InvestmentsChart from "./components/InvestmentsChart";
import NavigationList from "./components/NavigationList";
import RiskProfileTable from "./components/RiskProfileTable";
import SubHeader from "./components/SubHeader";
import "./styles.sass";

function getFirstKey(obj) {
  const keys = Object.keys(obj);
  return keys[0] || null;
}

function getDescription(index, language) {
  if (!index) {
    return "";
  }
  if (language === "en" && index?.description_en) {
    return index.description_en;
  }
  if (language === "he" && index.description) {
    return index.description;
  }
  return "";
}

export default function Index() {
  const dispatch = useDispatch();
  let { indexId } = useParams();

  if (indexId === "alphabeta-merger-arbitrage-index") {
    indexId = "100011";
  }

  const { t, i18n } = useTranslation();
  const isLtr = i18n.language === "en";

  const items = useSelector((state) => state?.data?.allItems);
  const index = useSelector((state) => state?.data?.activeIndex[0]);
  const benchmark = items?.find((obj) => obj.id === index?.parent_index);
  const indexTobenchmark = [index];

  if (benchmark) {
    indexTobenchmark.push(benchmark);
  } else {
    indexTobenchmark.push(null);
  }
  const today = new Date();
  const notes = index?.announcements?.filter(
    (entry) => new Date(entry.date) > today
  );
  const note = notes && notes.length > 0 ? notes[0]["text"] : null;
  const indexDescription = getDescription(index, i18n.language);

  React.useEffect(() => {
    dispatch(getIndexAction(indexId));
  }, [dispatch, indexId]);

  return (
    <main className={`main`} dir={isLtr ? "ltr" : "rtl"}>
      {index && (
        <>
          <h1 dir={"ltr"} className="index-h1">
            {index.name}
          </h1>
          <NavigationList />
          {indexDescription &&(
            <>
              <SubHeader text={t("AboutTable.index_description")} anchor="" />
              <p className="pSecond mb-30" dir={isLtr ? "ltr" : "rtl"}>
                {indexDescription}
              </p>
            </>
          )}
          <SubHeader text={t("NavigationList.index_details")} anchor="1" />
          <AboutTable index={index} />

          {note && note.length > 0 && (
            <>
              <SubHeader text={t("NavigationList.notes")} anchor="" />
              <p className="mb-30" dir={isLtr ? "ltr" : "rtl"}>
                {note}
              </p>
            </>
          )}
          <SubHeader
            text={t("NavigationList.current_composition_as_of")}
            anchor="2"
            date={getFirstKey(index.compositions)}
          />
          <CurrentCompTable
            compositions={index.compositions[getFirstKey(index.compositions)]}
          />

          {index.expected_composition.length > 0 && (
            <>
              <SubHeader
                text={t("NavigationList.expected_composition_on")}
                anchor=""
                date={handleDate(index.expected_composition[0].update)}
              />
              <ExpectedCompTable
                compositions={index.expected_composition[0].composition}
              />
            </>
          )}

          <SubHeader
            text={t("NavigationList.index_performance_to_date")}
            anchor="3"
          />
          <span className="small-text">
            {t("NavigationList.average_annual_return_15_years")}
          </span>
          <ColumnChart items={indexTobenchmark} />
          <span className="small-text">
            {t("NavigationList.periodic_returns")}
          </span>
          <Table items={indexTobenchmark} familyId={null} />

          <SubHeader
            text={t("NavigationList.theoretical_investment_of_from")}
            anchor="4"
            date={handleDate(index.base_date)}
          />
          <InvestmentsChart items={indexTobenchmark} />

          <SubHeader
            text={t("NavigationList.risk_profile_15_years")}
            anchor="5"
          />
          <RiskProfileTable items={indexTobenchmark} familyId={null} />

          <SubHeader text={t("NavigationList.daily_data")} anchor="" />
          <DailyDataCalendar index={index} />
        </>
      )}
      <p className="index-text-footer mt-50" dir={isLtr ? "ltr" : "rtl"}>
        {t("Index.footer_text")}
      </p>
      <PrintButton />
    </main>
  );
}
